import React, { useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { toast } from 'sonner';
import { isValidUSPhone } from '../../../utils/phoneValidation';
import { validateOhioAddress } from '../../../utils/addressValidation';

const EstimateFormStyle = () => (
  <style>{`
    

    .ef-wrap {
      background-color: white;
      font-family: hauora-medium, sans-serif;
      color: #3D3B6D;
      overflow: hidden;
    }

    .ef-heading {
      font-family: hauora-medium, sans-serif;
      color: #3D3B6D;
    }

    .ef-input,
    .ef-input-red {
      font-family: hauora-medium, sans-serif;
      font-size: 14px;
      border-color: #ced4da;
      padding: 12px 12px;
      height: 50px;
      color: #9a9ab0;
    }

    textarea.ef-input {
      height: auto;
    }

    .ef-input-error {
      border-color: #dc3545 !important;
    }

    .ef-input::placeholder,
    .ef-input-red::placeholder {
      color: #9a9ab0;
    }

    .ef-input:focus,
    .ef-input-red:focus {
      border-color: #b22335;
      box-shadow: 0 0 0 0.2rem rgba(178, 35, 53, 0.15);
      outline: none;
    }

    .ef-input option {
      font-family: hauora-medium, sans-serif;
      color: #3D3B6D;
    }

    .ef-submit-btn {
      background-color: #b22335;
      border-color: #b22335;
      color: #ffffff;
      border-radius: 50px;
      transition: all 0.3s ease;
      font-family: 'hauora-medium', sans-serif;
      font-size: 15px;
      white-space: nowrap;
      padding: 12px 40px;
    }

    .ef-submit-btn:hover {
      background-color: #3D3B6D;
      border-color: #3D3B6D;
      color: #ffffff;
      transform: translateY(-2px);
    }

    .ef-map-wrap {
      height: 100%;
      min-height: 400px;
    }

    .ef-map-wrap iframe {
      border: 0;
      border-radius: 12px;
      width: 100%;
      height: 100%;
      min-height: 400px;
    }

    @media (max-width: 991px) {
      .ef-map-wrap {
        min-height: 350px;
        margin-top: 30px;
      }
    }

    @media (max-width: 576px) {
      .ef-submit-btn {
        width: 100%;
      }

      .ef-wrap {
        padding: 20px !important;
      }
    }
  `}</style>
);

const EstimateForm = () => {
  const getApiUrl = (path) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8081' : 'https://ultimatesroofing.com/api';
    return `${base}${path}`;
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    address: '',
    source: '',
    message: '',
  });

  const [addressError, setAddressError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidUSPhone(formData.phone)) {
      toast.error('Please enter a valid US phone number');
      return;
    }

    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setAddressError(false);
    setLoading(true);

    const addrResult = await validateOhioAddress(formData.address);
    if (!addrResult.valid) {
      setAddressError(true);
      toast.error(addrResult.error);
      setLoading(false);
      return;
    }

    try {
      // Split full name into first name and last name for the backend
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const customerPayload = {
        firstName,
        lastName,
        phoneNumber: formData.phone,
        email: formData.email,
        service: formData.service,
        address: formData.address,
        message: formData.message || '',
        source: formData.source || '',
      };

      const formPayload = new FormData();
      formPayload.append('customerJson', JSON.stringify(customerPayload));

      const response = await fetch(
        getApiUrl('/ultimates/customer/register'),
        {
          method: 'POST',
          body: formPayload,
        }
      );

      const text = await response.text();

      if (response.ok) {
        toast.success('Form submitted successfully!');

        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          address: '',
          source: '',
          message: '',
        });
      } else {
        toast.error(text || 'Submission failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong!');
    }

    setLoading(false);
  };

  return (
    <div className="bg-white cs-section">
      <EstimateFormStyle />

      <Container className="p-4 p-lg-5 border rounded shadow-sm ef-wrap bg-white">
        <Row className="align-items-stretch">

          {/* Form Side */}
          <Col lg={6} className="d-flex flex-column justify-content-center">
            <h2 className="cs-title cs-title-to-content ef-heading">Get A Free Estimate</h2>

            <Form onSubmit={handleSubmit}>
              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Name"
                    className="ef-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Col>

                <Col sm={6} className="mb-3">
                  <Form.Control
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    className="ef-input"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Col>
              </Row>

              <Row>
                <Col sm={6} className="mb-3">
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Email"
                    className="ef-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Col>

                <Col sm={6} className="mb-3">
                  <Form.Select
                    name="service"
                    className="ef-input"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>Select Service</option>
                    <option>Residential Roofing</option>
                    <option>Commercial Roofing</option>
                    <option>Siding Service</option>
                    <option>Gutters System</option>
                    <option>Window Service</option>
                    <option>Painting Service</option>
                    <option>Flooring Service</option>
                    <option>Power Wash</option>
                    <option>Renovation Service</option>
                  </Form.Select>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  name="address"
                  placeholder="Property Address"
                  className={addressError ? 'ef-input ef-input-error' : 'ef-input'}
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Row>
                <Col sm={12} className="mb-3">
                  <Form.Select
                    name="source"
                    className="ef-input"
                    value={formData.source}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      How did you hear about us?
                    </option>
                    <option>Google Search</option>
                    <option>Google Ads</option>
                    <option>Facebook</option>
                    <option>Instagram</option>
                    <option>Referral</option>
                    <option>Other</option>
                  </Form.Select>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  placeholder="Your Roofing Needs & Concerns (Please Include Roof Type)"
                  className="ef-input"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Button
                type="submit"
                className="ef-submit-btn border-0"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Now'}
              </Button>
            </Form>
          </Col>

          {/* Map Side */}
          <Col lg={6}>
            <div className="ef-map-wrap">
              <iframe
                title="Gahanna Roofing Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3051.2429933079384!2d-83.09124142448769!3d40.11458797397414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8838edab7ce47dc9%3A0x2c289fc57a1cc5e9!2sResidential%20%26%20Commercial%20Roofing%20Company%20Across%20Ohio%20%7C%20Ultimates%20Roofing!5e0!3m2!1sen!2sca!4v1777047071716!5m2!1sen!2sca"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Col>

        </Row>
      </Container>
    </div>
  );
};

export default EstimateForm;